import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

export interface IReplaceTemplateIndustriesParams {
	templateId: string;
	industries: string[];
	status?: Api.TemplateStatus;
}

export interface IReplaceTemplateIndustriesOptions {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
}

/**
 * Admin: Replace all industry associations for a template.
 * Removes existing associations and sets new ones with the specified status.
 *
 * @param options - Mutation options including success/error callbacks
 * @returns React Query mutation result
 */
export const useReplaceTemplateIndustriesMutation = (options: IReplaceTemplateIndustriesOptions = {}) => {
	const { onError, onSuccess } = options;
	const userSession = useAdminUserSession();

	return useMutation({
		mutationFn: (params: IReplaceTemplateIndustriesParams) => {
			const { templateId, industries, status } = params;

			// Build URL with status as query parameter
			const urlPath =
				status !== undefined
					? `admin/surveytemplate/${templateId}/Industry?status=${status}`
					: `admin/surveytemplate/${templateId}/Industry`;

			return userSession.webServiceHelper.callAsync<void>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath,
				}),
				'PUT',
				industries // Send just the array, not wrapped in an object
			);
		},
		onSuccess: () => {
			// Invalidate relevant queries
			reactQueryClient.invalidateQueries({ queryKey: ['systemSurveyTemplate'] });
			reactQueryClient.invalidateQueries({ queryKey: ['surveyTemplatesByIndustry'] });
			onSuccess?.();
		},
		onError,
	});
};
